import React, { useState, useEffect, useCallback } from 'react';
import { OperaWindow, OperaSection, type SideButton } from './OperaWindow';
import {
  api,
  staffAvailabilityApi,
  ApiError,
  type ProfileListItem,
  type StaffReservation,
} from '../lib/api';

/**
 * Manager rosters a provider onto a shift directly, for when she rings in
 * rather than declaring through the portal. Creates a pre-approved SHIFT
 * declaration on her behalf.
 *
 * Two confirmations come back from the server as 409s and are answered by
 * re-posting with a flag, so neither can happen by accident:
 *   PAST_DATE           — rostering retrospectively (someone worked last
 *                         night and it was never logged)
 *   DECLARATION_EXISTS  — she already has a declaration that day, which the
 *                         unique key on (profile, property, date) won't allow
 *                         two of. Replacing overwrites it, so we say what it
 *                         is first.
 */

interface Props {
  shift: StaffReservation;
  onClose: () => void;
  onAssigned: (updated: StaffReservation) => void;
  /** A fully-open slot has no filled card to click, so edit lives here too. */
  onEditReservation: () => void;
}

interface Confirm {
  kind: 'PAST_DATE' | 'DECLARATION_EXISTS';
  message: string;
  profile: ProfileListItem;
}

export function AssignProviderModal({ shift, onClose, onAssigned, onEditReservation }: Props) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<ProfileListItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirm, setConfirm] = useState<Confirm | null>(null);

  // Flags persist across retries: a past-date shift whose provider also has an
  // existing declaration needs both answered before the assign goes through.
  const [confirmPast, setConfirmPast] = useState(false);
  const [replace, setReplace] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // api.profiles is scoped to the caller's currentPropertyId server-side,
      // which is what keeps this list to providers at this property.
      //
      // NOTE: it filters Profile.propertyId, while declarations are keyed to
      // propertyId and the *portal* writes them against
      // Profile.assignedPropertyId. Nothing diverges today (most profiles have
      // no assignedPropertyId at all), but if a provider is ever assigned to a
      // property other than her profile's home, a declaration created here
      // won't appear in her portal. Matching note on the assign endpoint.
      const list = await api.profiles(query.trim(), false);
      setResults(list);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [query]);

  useEffect(() => {
    const t = setTimeout(load, query ? 250 : 0);
    return () => clearTimeout(t);
  }, [load]);

  async function assign(profile: ProfileListItem, flags: { past: boolean; replace: boolean }) {
    setBusy(true);
    setError(null);
    try {
      const updated = await staffAvailabilityApi.assignProvider(shift.id, {
        profileId: profile.id,
        confirmPast: flags.past || undefined,
        replace: flags.replace || undefined,
      });
      onAssigned(updated);
    } catch (e) {
      if (e instanceof ApiError && (e.code === 'PAST_DATE' || e.code === 'DECLARATION_EXISTS')) {
        setConfirm({ kind: e.code, message: e.message, profile });
      } else {
        setError(e instanceof Error ? e.message : String(e));
      }
      setBusy(false);
    }
  }

  function acceptConfirm() {
    if (!confirm) return;
    const past = confirmPast || confirm.kind === 'PAST_DATE';
    const rep = replace || confirm.kind === 'DECLARATION_EXISTS';
    setConfirmPast(past);
    setReplace(rep);
    setConfirm(null);
    void assign(confirm.profile, { past, replace: rep });
  }

  const sideButtons: SideButton[] = [
    { label: 'Edit spot…', onClick: onEditReservation, disabled: busy },
    { label: 'Close',       onClick: onClose,     disabled: busy },
  ];

  return (
    <OperaWindow
      title="ASSIGN PROVIDER"
      status={`${shift.date} · ${shift.startTime}-${shift.endTime} · ${shift.holder ? `held by ${shift.holder.workingName}` : 'open'}`}
      onClose={onClose}
      sideButtons={sideButtons}
    >
      {error && <div className="modal-error">{error}</div>}

      {confirm ? (
        <OperaSection title={confirm.kind === 'PAST_DATE' ? 'Confirm past date' : 'Replace declaration'}>
          <p style={{ margin: '4px 0 12px', fontSize: 12 }}>{confirm.message}</p>
          {confirm.kind === 'DECLARATION_EXISTS' && (
            <p style={{ margin: '0 0 12px', fontSize: 12, color: 'var(--text-secondary)' }}>
              Replacing overwrites that declaration with this shift. Her
              original request or blackout will be gone.
            </p>
          )}
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              type="button"
              className="avail-button-approve"
              onClick={acceptConfirm}
              disabled={busy}
            >
              {confirm.kind === 'PAST_DATE' ? 'Roster anyway' : 'Replace'}
            </button>
            <button
              type="button"
              className="avail-button-reject"
              onClick={() => setConfirm(null)}
              disabled={busy}
            >
              Back
            </button>
          </div>
        </OperaSection>
      ) : (
        <>
          <OperaSection title="Find provider">
            <div className="profile-search-bar">
              <input
                type="text"
                value={query}
                onChange={e => setQuery(e.target.value)}
                placeholder="Search by name, phone, or email…"
                autoFocus
                disabled={busy}
              />
            </div>
          </OperaSection>

          <div className="assign-results">
            {loading && <div className="loading">Loading…</div>}
            {!loading && results.length === 0 && (
              <div className="loading">
                No providers at this property match{query ? ` "${query}"` : ''}.
              </div>
            )}
            {!loading && results.map(p => (
              <button
                key={p.id}
                type="button"
                className="assign-result"
                onClick={() => assign(p, { past: confirmPast, replace })}
                disabled={busy}
              >
                <span className="assign-result-name">{p.workingName}</span>
                <span className="assign-result-contact">{p.phone || p.email || '—'}</span>
              </button>
            ))}
          </div>
        </>
      )}
    </OperaWindow>
  );
}
